import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { GlobalField } from 'shared/api/common/schemas/globalField'
import { NotificationReason } from 'shared/api/common/schemas/notificationReason'
import { Tag } from 'shared/api/common/schemas/tag'
import { CustomEvent } from 'shared/api/common/schemas/templates/customEvent'
import { UserField } from 'shared/api/common/schemas/userField'

import {
  fetchTargetUserFields,
  fetchTargetGlobalFields,
  fetchTargetPageOtns,
  fetchTargetPageTags,
  fetchTargetPageCustomEvents,
} from './mappingActions'

// Initial state
export interface TargetPageState {
  userFields: {
    items: UserField[] | null
    fetching?: boolean
  }
  tags: {
    items: Tag[] | null
    fetching?: boolean
  }
  globalFields: {
    items: GlobalField[] | null
    fetching?: boolean
  }
  otns: {
    items: NotificationReason[] | null
    fetching?: boolean
  }
  customEvents: {
    items: CustomEvent[] | null
    fetching?: boolean
  }
  tagsMap: Record<string, Tag>
  cufsMap: Record<string, UserField>
  gafsMap: Record<string, GlobalField>
  otnsMap: Record<string, NotificationReason>
  customEventsMap: Record<string, CustomEvent>
}

const initialState: TargetPageState = {
  userFields: { items: null },
  tags: { items: null },
  globalFields: { items: null },
  otns: { items: null },
  customEvents: { items: null },
  tagsMap: {},
  cufsMap: {},
  gafsMap: {},
  otnsMap: {},
  customEventsMap: {},
}

// Slice
const mappingSlice = createSlice({
  name: 'mapping',
  initialState,
  reducers: {
    updateOtnsMap: (state, action: PayloadAction<Record<string, NotificationReason>>) => {
      state.otnsMap = { ...state.otnsMap, ...action.payload }
    },
    updateTagsMap: (state, action: PayloadAction<Record<string, Tag>>) => {
      state.tagsMap = { ...state.tagsMap, ...action.payload }
    },
    updateCUFsMap: (state, action: PayloadAction<Record<string, UserField>>) => {
      state.cufsMap = { ...state.cufsMap, ...action.payload }
    },
    updateGAFsMap: (state, action: PayloadAction<Record<string, GlobalField>>) => {
      state.gafsMap = { ...state.gafsMap, ...action.payload }
    },
    updateCustomEventsMap: (state, action: PayloadAction<Record<string, CustomEvent>>) => {
      state.customEventsMap = { ...state.customEventsMap, ...action.payload }
    },
    resetMaps: (state) => {
      state.tagsMap = {}
      state.cufsMap = {}
      state.gafsMap = {}
      state.otnsMap = {}
      state.customEventsMap = {}
    },
  },
  extraReducers: (builder) => {
    // User Fields
    builder
      .addCase(fetchTargetUserFields.fulfilled, (state, action) => {
        state.userFields.items = action.payload
      })
      .addCase(fetchTargetUserFields.pending, (state) => {
        state.userFields.items = null
      })

    // Global Fields
    builder
      .addCase(fetchTargetGlobalFields.fulfilled, (state, action) => {
        state.globalFields.items = action.payload
      })
      .addCase(fetchTargetGlobalFields.pending, (state) => {
        state.globalFields.items = null
      })

    // OTNs
    builder
      .addCase(fetchTargetPageOtns.fulfilled, (state, action) => {
        state.otns.items = action.payload
      })
      .addCase(fetchTargetPageOtns.pending, (state) => {
        state.otns.items = null
      })

    // Tags
    builder
      .addCase(fetchTargetPageTags.fulfilled, (state, action) => {
        state.tags.items = action.payload
        state.tags.fetching = false
      })
      .addCase(fetchTargetPageTags.pending, (state) => {
        state.tags.items = null
        state.tags.fetching = true
      })
      .addCase(fetchTargetPageTags.rejected, (state) => {
        state.tags.items = []
        state.tags.fetching = false
      })

    // Custom Events
    builder
      .addCase(fetchTargetPageCustomEvents.fulfilled, (state, action) => {
        state.customEvents.items = action.payload
      })
      .addCase(fetchTargetPageCustomEvents.pending, (state) => {
        state.customEvents.items = null
      })
  },
})

export const {
  updateOtnsMap,
  updateTagsMap,
  updateCUFsMap,
  updateGAFsMap,
  updateCustomEventsMap,
  resetMaps,
} = mappingSlice.actions

export const { reducer } = mappingSlice
